import React from "react";
import moment from "moment";
import ModalComponent from "../../../components/Modal";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import { Formik, Form } from "formik";
import MuiDatePicker from "../../../components/MuiDatePicker";
import { FaUpload, FaFile, FaTimes, FaDownload, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { downloadCandidateDocument } from "../../../Services/candidateDocuments";
import CustomDropdown from "../../../components/CustomDropdown";

const EditSubmissionModal = ({
  show,
  onClose,
  initialValues,
  isLoading,
  isSaving,
  onSubmit,
  candidateResumes = [],
  resumeAction,
  onResumeActionChange,
  selectedResumeId,
  onSelectResume,
  resumeFile,
  onResumeFileChange,
  resumeError,
  currentResume,
  isCheckingResume,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileChange,
  onDeleteFile,
}) => {
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDownloadResume = async () => {
    if (!currentResume?.fileName) {
      toast.error("Resume file name not available");
      return;
    }

    try {
      const downloadResponse = await downloadCandidateDocument(currentResume.fileName);
      
      if (!downloadResponse?.base64Data) {
        toast.error("Download link not available");
        return;
      }

      const filename = downloadResponse.originalFilename || currentResume.fileName || "resume";
      const link = window.document.createElement("a");
      link.href = downloadResponse.base64Data;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      toast.error(error?.message || "Failed to download resume");
    }
  };
  return (
    <ModalComponent
      show={show}
      onClose={onClose}
      title="Edit Submission"
    >
      {isLoading ? (
        <div className="py-12 text-center text-sm text-gray-500">
          Loading submission details...
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSubmit(values);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, handleChange, isSubmitting, setFieldValue }) => (
            <Form className="max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                <div className="sm:col-span-1 col-span-2 space-y-1">
                  <TextInput
                    name="rate"
                    legend="Rate"
                    placeholder="Rate"
                    value={values.rate}
                    handleChange={handleChange}
                    type="text"
                    classes="w-full"
                  />
                
                </div>
                <div className="sm:col-span-1 col-span-2 space-y-1">
                  <TextInput
                    name="rateType"
                    legend="Rate Type"
                    placeholder="Rate Type"
                    value={values.rateType}
                    handleChange={handleChange}
                    type="text"
                    classes="w-full"
                  />
                  
                </div>
                <div className="sm:col-span-1 col-span-2">
                  <MuiDatePicker
                    label="Scheduled Date"
                    value={
                      values?.scheduledDate
                        ? moment(
                            values.scheduledDate,
                            ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"],
                            true
                          )
                        : null
                    }
                    onChange={(date) => {
                      if (date) {
                        setFieldValue("scheduledDate", date.format("YYYY-MM-DD"));
                      } else {
                        setFieldValue("scheduledDate", "");
                      }
                    }}
                  />
                </div>
                <div className="sm:col-span-1 col-span-2">
                  <TextInput
                    name="scheduledTime"
                    legend="Scheduled Time"
                    placeholder="Scheduled Time"
                    value={values.scheduledTime || ""}
                    handleChange={handleChange}
                    type="time"
                    classes="w-full"
                  />
                </div>
                <div className="sm:col-span-1 col-span-2 space-y-2">
                  <span className="text-sm font-medium text-gray-700">Status</span>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="radio"
                        name="status"
                        value="A"
                        checked={values.status === "A"}
                        onChange={handleChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="radio"
                        name="status"
                        value="D"
                        checked={values.status === "D"}
                        onChange={handleChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      Inactive
                    </label>
                  </div>
    
                </div>
                <div className="sm:col-span-1 col-span-2 space-y-1">
                  <TextInput
                    name="stage"
                    legend="Stage"
                    placeholder="Stage"
                    value={values.stage || ""}
                    handleChange={handleChange}
                    type="text"
                    classes="w-full"
                  />
                </div>
                <div className="sm:col-span-2 col-span-2 space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Resume</span>
                  </div>

                  {/* Display existing submission resume file */}
                  {currentResume && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">Current Submission Resume:</h4>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <FaFile className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate text-left">
                              {currentResume.fileName || "Attached resume"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {currentResume.fromCandidateDocument
                                ? "Linked from candidate documents"
                                : "Uploaded directly for this submission"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={handleDownloadResume}
                            className="text-blue-500 hover:text-blue-700 transition-colors p-1 rounded"
                            title="Download file"
                            disabled={isSaving}
                          >
                            <FaDownload className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!currentResume && (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                      No resume is currently associated with this submission.
                    </div>
                  )}

                  {/* Display newly selected file */}
                  {resumeFile && resumeAction === "UPLOAD_NEW" && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">New Resume File:</h4>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <FaFile className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {resumeFile.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(resumeFile.size || 0)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={onDeleteFile}
                            className="text-rose-500 hover:text-rose-700 transition-colors p-1 rounded"
                            title="Remove file"
                            disabled={isSaving}
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mb-3">
                    {currentResume 
                      ? "Select an option below to replace the current resume, or leave unchanged to keep it."
                      : "Select an option below to add a resume."}
                  </p>
                  {isCheckingResume ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                      Loading candidate resumes...
                    </div>
                  ) : (
                    <>
                      {candidateResumes.length > 0 ? (
                        <div className="space-y-3 rounded-md border border-gray-200 p-3">
                          <div 
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              if (resumeAction === "USE_EXISTING") {
                                // Deselect if already selected
                                onResumeActionChange?.(null);
                                onSelectResume?.("");
                              } else {
                                // Select this option
                                onResumeActionChange?.("USE_EXISTING");
                              }
                            }}
                          >
                            <input
                              type="radio"
                              id="edit-resume-use-existing"
                              name="edit-resume-action"
                              value="USE_EXISTING"
                              checked={resumeAction === "USE_EXISTING"}
                              onChange={() => {
                                // Handled by parent div onClick
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (resumeAction === "USE_EXISTING") {
                                  // Deselect if already selected
                                  onResumeActionChange?.(null);
                                  onSelectResume?.("");
                                } else {
                                  // Select this option
                                  onResumeActionChange?.("USE_EXISTING");
                                }
                              }}
                              className="h-4 w-4 cursor-pointer"
                              disabled={isSaving}
                            />
                            <label 
                              htmlFor="edit-resume-use-existing" 
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Use an existing candidate resume
                            </label>
                          </div>
                          <CustomDropdown
                            value={candidateResumes
                              .map((doc) => ({ value: doc.id, label: doc.originalFilename || doc.fileName || `Resume ${doc.id}` }))
                              .find((option) => String(option.value) === String(selectedResumeId || ""))}
                            name="selectedResumeId"
                            handleChange={(option) => onSelectResume?.(option?.value || "")}
                            placeholder="Select a resume"
                            options={candidateResumes.map((doc) => ({
                              value: doc.id,
                              label: doc.originalFilename || doc.fileName || `Resume ${doc.id}`,
                            }))}
                            handleGetOptionValue={(opt) => opt.value}
                            handleGetOptionName={(opt) => opt.label}
                            isDisabled={isSaving || resumeAction !== "USE_EXISTING"}
                            customClasses="w-full"
                          />
                        </div>
                      ) : (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                          No saved resumes found for this candidate.
                        </div>
                      )}
                      <div className="space-y-2 rounded-md border border-gray-200 p-2">
                        <div 
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            if (resumeAction === "UPLOAD_NEW") {
                              // Deselect if already selected
                              onResumeActionChange?.(null);
                              onDeleteFile?.();
                            } else {
                              // Select this option
                              onResumeActionChange?.("UPLOAD_NEW");
                            }
                          }}
                        >
                          <input
                            type="radio"
                            id="edit-resume-upload"
                            name="edit-resume-action"
                            value="UPLOAD_NEW"
                            checked={resumeAction === "UPLOAD_NEW"}
                            onChange={() => {
                              // Handled by parent div onClick
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (resumeAction === "UPLOAD_NEW") {
                                // Deselect if already selected
                                onResumeActionChange?.(null);
                                onDeleteFile?.();
                              } else {
                                // Select this option
                                onResumeActionChange?.("UPLOAD_NEW");
                              }
                            }}
                            className="h-4 w-4 cursor-pointer"
                            disabled={isSaving}
                          />
                          <label 
                            htmlFor="edit-resume-upload" 
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            Upload a different resume
                          </label>
                        </div>
                        {resumeAction === "UPLOAD_NEW" && !resumeFile && (
                          <div className="mt-1">
                            <div
                              className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${
                                isDragging
                                  ? "border-blue-500 bg-blue-50/50"
                                  : "border-gray-300 hover:border-blue-500/50 bg-gray-50/30"
                              } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                              onDrop={onDrop}
                              onDragOver={onDragOver}
                              onDragLeave={onDragLeave}
                            >
                              <div className="p-2 text-center">
                                <div className="flex justify-center mb-1">
                                  <div
                                    className={`p-1 rounded-full ${
                                      isDragging ? "bg-blue-100" : "bg-gray-100"
                                    }`}
                                  >
                                    <FaUpload
                                      className={`w-4 h-4 ${
                                        isDragging ? "text-blue-600" : "text-gray-400"
                                      }`}
                                    />
                                  </div>
                                </div>

                                <h4 className="text-xs font-medium text-gray-700 mb-0.5">
                                  {isDragging ? "Drop file here" : ""}
                                </h4>

                                <p className="text-xs text-gray-500 mb-1.5">
                                  Drag and drop resume file here, or click to browse
                                </p>

                                <label className="inline-flex items-center px-2.5 py-1 bg-[#201F1E] text-white text-xs font-medium rounded-lg hover:bg-[#201F1E] transition-colors cursor-pointer">
                                  <FaUpload className="w-3 h-3 mr-1" />
                                  Choose File
                                  <input
                                    type="file"
                                    accept=".pdf,.docx,.png,.jpg,.jpeg,.bmp,.gif"
                                    className="hidden"
                                    onChange={onFileChange}
                                    disabled={isSaving}
                                  />
                                </label>
                                <p className="text-xs text-gray-400 mt-1">
                                  Supported formats: PDF, DOCX, PNG, JPG, JPEG, BMP, GIF (Max 10MB)
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {resumeError && (
                    <p className="text-xs text-red-600 mt-2">{resumeError}</p>
                  )}
                </div>
                <div className="sm:col-span-2 col-span-2 space-y-1">
                  <TextArea
                    name="notes"
                    // legend="Notes"
                  //  placeholder="Notes"
                    value={values.notes}
                    handleChange={handleChange}
                    classes="w-full"
                    rows={4}
                    maxLength={250}
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {(values.notes || "").length}/250 characters
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || isSubmitting}
                  className="px-4 py-2 bg-[#201F1E] text-white rounded-md hover:bg-[#201F1E] disabled:opacity-50"
                >
                  {isSaving || isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </ModalComponent>
  );
};

export default EditSubmissionModal;
